import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  AvailablePlan,
  getRaceTypeFormats,
  getRaceTypes,
  getSelectablePlans,
  RaceType,
  RaceTypeFormat,
} from '@services/planCatalogService';
import {
  AthleteSettings,
  CheckSettingsResult,
  DateBounds,
  EngineErrorBody,
  SettingsForm,
  checkSettings,
  getDateBounds,
  getSettingsForm,
} from '@services/engineService';
import { queryKeys } from './queryKeys';

export const useRaceTypes = () => {
  const { data, isLoading } = useQuery<RaceType[]>({
    queryKey: queryKeys.raceTypes,
    queryFn: getRaceTypes,
    staleTime: 60 * 60 * 1000,
  });
  return { raceTypes: data ?? [], isLoading };
};

export const useRaceTypeFormats = (raceTypeId?: number, enabled = true) => {
  const { data, isLoading } = useQuery<RaceTypeFormat[]>({
    queryKey: queryKeys.raceTypeFormats(raceTypeId),
    queryFn: () => getRaceTypeFormats(raceTypeId!),
    enabled: enabled && raceTypeId !== undefined,
    staleTime: 60 * 60 * 1000,
  });
  return { formats: data ?? [], isLoading: enabled && raceTypeId !== undefined && isLoading };
};

/** UPDATE-20: the plans of one (race type, format) — `enabled` waits for the format when the type has some. */
export const useSelectablePlans = (raceTypeId: number | undefined, formatId: string | undefined, enabled: boolean) => {
  const { data, isLoading } = useQuery<AvailablePlan[]>({
    queryKey: queryKeys.selectablePlans(raceTypeId, formatId),
    queryFn: () => getSelectablePlans(raceTypeId!, formatId),
    enabled: enabled && raceTypeId !== undefined,
    staleTime: 5 * 60 * 1000,
  });
  return { plans: data ?? [], isLoading: enabled && raceTypeId !== undefined && isLoading };
};

export const useSettingsForm = (planId?: string) => {
  const { data, isLoading, error } = useQuery<SettingsForm>({
    queryKey: queryKeys.settingsForm(planId),
    queryFn: () => getSettingsForm(planId!),
    enabled: !!planId,
    staleTime: 0,
    refetchOnMount: 'always',
  });
  return { settingsForm: data ?? null, isLoading, error };
};

/**
 * F4 — the picker's bounds are always computed with tests **on**. Turning them off later only
 * shortens the minimum, so a date chosen against these bounds stays valid, and refetching on the
 * switch would move the calendar under the athlete for no gain.
 */
export const useDateBounds = (planId?: string) => {
  const { data, isLoading, error } = useQuery<DateBounds | { bounds: null }>({
    queryKey: queryKeys.dateBounds(planId),
    queryFn: () => getDateBounds(planId!),
    enabled: !!planId,
    staleTime: 5 * 60 * 1000,
  });
  const bounds = data && !('bounds' in data) ? data : null;
  return { bounds, isLoading, error };
};

/**
 * PRD 6 §6 — feasibility on every change, debounced 300 ms. A query would key on the settings and
 * cache every intermediate state the athlete passes through; this is a live check, not a resource.
 */
export const useCheckSettings = (planId: string | undefined, settings: AthleteSettings, enabled: boolean) => {
  const [error, setError] = useState<EngineErrorBody | null>(null);
  const [checking, setChecking] = useState(false);
  const serialised = JSON.stringify(settings);

  useEffect(() => {
    if (!planId || !enabled) {
      setError(null);
      return;
    }
    let cancelled = false;
    setChecking(true);
    const timer = setTimeout(() => {
      checkSettings(planId, JSON.parse(serialised) as AthleteSettings)
        .then((result: CheckSettingsResult) => {
          if (cancelled) return;
          setError(result.ok ? null : result.error);
        })
        .catch(() => {
          // A failed check must not silently unblock « Suivant ».
          if (!cancelled) setError({ code: 'CHECK_FAILED', message: 'Vérification impossible pour le moment.' });
        })
        .finally(() => {
          if (!cancelled) setChecking(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [planId, serialised, enabled]);

  return { error, checking };
};
